import Radio from '@/components/Radio';
import { CaretDown } from '@phosphor-icons/react';
import Fade from '@mui/material/Fade';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { twJoin, twMerge } from 'tailwind-merge';
import React from 'react';
import spinner from '@/assets/spinner-blue.gif';
import { getPostProcessing } from './FilterDropDownPillTableNew';

function trimToTwoDecimals(num = 0) {
  const decimals = num.toString().split('.')[1];
  if (!decimals || decimals.length < 2) {
    return num;
  }
  return (Math.floor(num * 100) / 100).toFixed(2);
}

export default function TableStats({
  totalRowCount,
  activeColumn,
  tableColumnStats,
  schema,
  msgId,
  name,
  stepId,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [valueContainer, setValueContainer] = React.useState(null);
  const open = Boolean(anchorEl);
  const id = `table-stats-popper-${msgId}-${name}-${stepId}-${activeColumn}`;
  const isLoading = tableColumnStats.isLoading || tableColumnStats.isFetching;
  const stats = tableColumnStats.data?.data || { count: 0 };
  const options = Object.keys(stats).reduce((acc, key) => {
    const value =
      key !== 'count'
        ? getPostProcessing(
            trimToTwoDecimals(stats[key] || 0),
            schema,
            activeColumn
          )
        : stats[key];
    acc.push({
      key,
      text: key.toLocaleUpperCase(),
      value: typeof value === 'number' ? value.toLocaleString('en-US') : value,
    });
    return acc;
  }, []);
  const isNumeric =
    Object.keys(stats).length > 0 && Object.keys(stats)[0] !== 'count';

  React.useEffect(() => {
    setAnchorEl(null);
    setValueContainer({
      key: 'count',
      text: 'COUNT',
      value: (stats['count'] || 0).toLocaleString('en-US'),
    });
  }, [isLoading, activeColumn]);

  return (
    <>
      <div
        {...(activeColumn &&
          isNumeric && {
            id: `table-stats-${msgId}-${name}-${stepId}-${activeColumn}`,
          })}
        className={twJoin(
          'flex items-center justify-between gap-1.5 px-2.5 py-0.5 rounded-lg',
          activeColumn && isNumeric && 'bg-pv-tags-blue cursor-pointer',
          isLoading && 'cursor-not-allowed'
        )}
        onClick={(e) => {
          if (activeColumn && !isLoading && isNumeric) {
            setAnchorEl(e.currentTarget);
          }
        }}
      >
        {isLoading && <img src={spinner} alt="spinner" className="w-3 h-3" />}
        <span
          className={twMerge(
            'text-xs text-brand-ai-1100 leading-4.5 select-none',
            isLoading && 'text-brand-ai-1000'
          )}
        >
          {valueContainer && activeColumn
            ? `${valueContainer.text}: ${valueContainer.value}`
            : `Total count: ${totalRowCount || 0}`}
        </span>
        {activeColumn && !isLoading && isNumeric && (
          <CaretDown
            className={twMerge(
              'text-pv-neutral-grey-900 duration-300 transition-transform',
              open && 'transform -rotate-180',
              isLoading && 'text-brand-ai-1000'
            )}
            weight="fill"
          />
        )}
      </div>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="top-start"
        transition
        style={{
          zIndex: 99,
        }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener
            onClickAway={() => {
              setAnchorEl(null);
            }}
          >
            <Fade {...TransitionProps} timeout={250}>
              <div
                className="rounded-lg bg-white shadow-popup"
                style={{ width: '260px' }}
              >
                {options.map((option) => (
                  <Radio
                    key={option.key}
                    id={option.key}
                    label={`${option.text} : ${option.value}`}
                    className={{
                      wrapper:
                        'px-4 py-3 hover:bg-pv-primary-primary-50 cursor-pointer',
                      label: 'text-sm text-brand-ai-200',
                    }}
                    checked={valueContainer?.key === option.key}
                    onChange={() => {
                      setValueContainer(option);
                      setAnchorEl(null);
                    }}
                  />
                ))}
              </div>
            </Fade>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
